import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

import { reportError } from '@/utils/reportError';

// Android SecureStore (EncryptedSharedPreferences) value limit is ~2KB.
// We chunk large payloads (Supabase session ≈ 3-4 KB) across multiple keys.
const CHUNK_SIZE = 1800;
const CHUNK_META_SUFFIX = '__meta';

function chunkKey(key: string, index: number): string {
  return `${key}__${index}`;
}

function metaKey(key: string): string {
  return `${key}${CHUNK_META_SUFFIX}`;
}

async function readChunked(key: string): Promise<string | null> {
  const meta = await SecureStore.getItemAsync(metaKey(key));
  if (!meta) {
    // Single-value (no meta) — read directly
    return SecureStore.getItemAsync(key);
  }
  const count = parseInt(meta, 10);
  if (!Number.isFinite(count) || count <= 0) return null;
  const parts: string[] = [];
  for (let i = 0; i < count; i++) {
    const part = await SecureStore.getItemAsync(chunkKey(key, i));
    if (part == null) return null; // corrupted — bail
    parts.push(part);
  }
  return parts.join('');
}

async function writeChunked(key: string, value: string): Promise<void> {
  if (value.length <= CHUNK_SIZE) {
    // Small enough — clear any old chunked state then write single
    await clearChunked(key);
    await SecureStore.setItemAsync(key, value);
    return;
  }
  // Clear single-value form if any
  await SecureStore.deleteItemAsync(key);
  // Write chunks
  const count = Math.ceil(value.length / CHUNK_SIZE);
  for (let i = 0; i < count; i++) {
    const slice = value.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    await SecureStore.setItemAsync(chunkKey(key, i), slice);
  }
  await SecureStore.setItemAsync(metaKey(key), String(count));
}

async function clearChunked(key: string): Promise<void> {
  const meta = await SecureStore.getItemAsync(metaKey(key));
  if (meta) {
    const count = parseInt(meta, 10);
    if (Number.isFinite(count) && count > 0) {
      for (let i = 0; i < count; i++) {
        await SecureStore.deleteItemAsync(chunkKey(key, i));
      }
    }
    await SecureStore.deleteItemAsync(metaKey(key));
  }
  await SecureStore.deleteItemAsync(key);
}

/**
 * Storage adapter compatible with Supabase's `auth.storage` contract.
 *
 * Reads:  SecureStore first, with one-time migration from AsyncStorage if a
 *         legacy session exists. After migration, AsyncStorage is wiped for
 *         that key so we never read it again.
 * Writes: SecureStore only, chunked if needed.
 * Removes: clears both SecureStore (chunked) and any leftover AsyncStorage row.
 *
 * Migration is transparent: existing users keep their session after upgrade.
 */
const nativeSecureStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      const fromSecure = await readChunked(key);
      if (fromSecure != null) return fromSecure;

      // Legacy fallback — migrate from AsyncStorage on first read.
      const legacy = await AsyncStorage.getItem(key);
      if (legacy != null) {
        await writeChunked(key, legacy);
        await AsyncStorage.removeItem(key);
        return legacy;
      }
      return null;
    } catch {
      // Never throw out of storage layer — Supabase treats throw as "no session".
      return null;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    try {
      await writeChunked(key, value);
      // Defensive: ensure no stale AsyncStorage copy remains.
      await AsyncStorage.removeItem(key).catch(() => undefined);
    } catch (err) {
      // SecureStore failed. In DEV (simulators have Keychain quirks) we fall
      // back to AsyncStorage to keep the login flow usable. In PRODUCTION we
      // never write auth tokens to unencrypted storage: the session just
      // won't persist across restarts on this device, and we report the
      // failure so these cases are visible in Sentry.
      if (__DEV__) {
        try {
          await AsyncStorage.setItem(key, value);
        } catch {
          // Swallow — Supabase will re-fetch on next refresh
        }
        return;
      }
      reportError(err, { context: 'secureStorage.setItem failed — session not persisted' });
    }
  },

  async removeItem(key: string): Promise<void> {
    try {
      await clearChunked(key);
    } catch {
      // ignore
    }
    await AsyncStorage.removeItem(key).catch(() => undefined);
  },
};

// WEB : expo-secure-store n'existe pas dans le navigateur (SecureStore.* lève
// une erreur) → en prod l'adaptateur natif ne persistait PAS la session, donc
// la connexion « ne marchait pas » (retour au login). Sur web on s'appuie sur
// localStorage. Le natif (iOS/Android) reste sur le stockage chiffré, inchangé.
//
// Garde SSR : l'export statique (`output: 'static'`) pré-rend chaque route sous
// Node, où `window`/`localStorage` n'existent pas. On renvoie alors null / no-op
// (pas de session au pré-rendu) au lieu de planter le build.
const webStorage = {
  async getItem(key: string): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // ignore (quota / mode privé)
    }
  },
  async removeItem(key: string): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

export const secureStorage =
  Platform.OS === 'web' ? webStorage : nativeSecureStorage;
